import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { TintImage } from "@/components";
import {
  useResponsiveness,
  useScrollPosition,
  useTheme,
} from "@/providers/context";
import { cn, getTabIconFilename, toPascalCase } from "@/utils";
import { useAppProperties } from "@/hooks";

interface NavTabProps {
  active: boolean;
  tabId: number | string;
  name: string;
  icon: string;
  home?: boolean;
}

export const NavTab = ({
  tabId,
  home,
  active,
  name,
  icon,
  ...props
}: NavTabProps) => {
  const navigate = useNavigate();
  const { highlightHEX } = useTheme();
  const { condensedNavBar } = useResponsiveness();
  const { colorSchemeColors } = useAppProperties();
  const { resetScrollPositions } = useScrollPosition();

  const handleClick = useCallback(() => {
    resetScrollPositions();
    if (
      [
        "search",
        "favorites",
        "calendar",
        "watch",
        "playlists",
        "recent",
        "community",
      ].includes(tabId.toString())
    ) {
      navigate(`/${tabId}`);
    } else if (home) {
      navigate("/");
    } else {
      navigate(`/content/${tabId}`);
    }
  }, [home, navigate, tabId, resetScrollPositions]);

  return (
    <div
      className={cn([
        "p2-semibold mx-5 flex h-[40px] w-[calc(100%-40px)] shrink-0 cursor-pointer items-center gap-[8px] rounded-[8px] px-3 hover:bg-surface-secondary",
        active && "bg-highlightBg hover:bg-highlightBg",
      ])}
      onClick={handleClick}
      {...props}
    >
      {getTabIconFilename({ icon }) && (
        <TintImage
          src={getTabIconFilename({ icon }) as string}
          color={active ? highlightHEX : colorSchemeColors.grey700}
          dimensions={{ width: 20, height: 20 }}
        />
      )}
      {!condensedNavBar && (
        <span
          className={
            active ? "p2-bold text-highlightPrimary" : "text-item-secondary"
          }
        >
          {toPascalCase(name) ?? `Tab`}
        </span>
      )}
    </div>
  );
};
